import { Switch, Route, useLocation, Router as WouterRouter, Redirect } from "wouter";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { useApprovalStatus } from "@/hooks/use-approval-status";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ClerkProvider, SignIn, SignUp, useAuth, useClerk } from "@clerk/react";
import { publishableKeyFromHost } from "@clerk/react/internal";
import { shadcn } from "@clerk/themes";
import { useEffect, useRef } from "react";
import { setAuthTokenGetter } from "@workspace/api-client-react";
import { clearRheumHubLocalStorage } from "@/lib/local-storage-keys";
import { ErrorBoundary } from "@/components/error-boundary";

// HMR-safe global singleton — survives Vite hot-module replacement cycles.
// All HMR re-evaluations find the same object on window, so ClerkAuthTokenBridge
// always writes to (and the fetch interceptor always reads from) one stable ref.
const _GLOBAL_CLERK_REF = "__rheumhub_clerk_ref__";
if (!(window as unknown as Record<string, unknown>)[_GLOBAL_CLERK_REF]) {
  (window as unknown as Record<string, unknown>)[_GLOBAL_CLERK_REF] = { getToken: null };
}
const _clerkTokenRef = (window as unknown as Record<string, unknown>)[_GLOBAL_CLERK_REF] as {
  getToken: (() => Promise<string | null>) | null;
};

// Register once with the generated-hook fetch client.
setAuthTokenGetter(() => _clerkTokenRef.getToken?.() ?? null);

// Global fetch interceptor — injects Bearer token for all /api/* requests.
// Applied only once (guarded by __rheumhubPatched) so HMR re-evaluations don't
// stack multiple wrappers. Excludes /api/__clerk (Clerk's own proxy path).
if (!(window.fetch as unknown as Record<string, unknown>)["__rheumhubPatched"]) {
  const _origFetch = window.fetch.bind(window);
  const _patchedFetch = async function patchedFetch(
    input: RequestInfo | URL,
    init: RequestInit = {},
  ): Promise<Response> {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : (input as Request).url;
    const isOurApi =
      (url.startsWith("/api/") || url.includes("/api/")) &&
      !url.includes("/api/__clerk");
    if (isOurApi && _clerkTokenRef.getToken) {
      const token = await _clerkTokenRef.getToken().catch(() => null);
      if (token) {
        const headers = new Headers(init.headers);
        if (!headers.has("authorization")) {
          headers.set("authorization", `Bearer ${token}`);
        }
        init = { ...init, headers };
      }
    }
    return _origFetch(input, init);
  };
  (_patchedFetch as unknown as Record<string, unknown>)["__rheumhubPatched"] = true;
  window.fetch = _patchedFetch;
}

import { LocalStorageNotice } from "@/components/ui/local-storage-notice";
import { ClinicalSafetyModal } from "@/components/ui/clinical-safety-modal";
import NotFound from "@/pages/not-found";
import Landing from "@/pages/landing";
import ChooseRole from "@/pages/choose-role";
import OnboardingFlow from "@/pages/onboarding-flow";
import ProfileEditor from "@/pages/profile-editor";
import PilotWelcomeBanner from "@/components/pilot-welcome-banner";
import Onboarding from "@/pages/onboarding/index";
import PrivacyPolicy from "@/pages/privacy";
import TermsOfUse from "@/pages/terms";
import ClinicalTools from "@/pages/clinical-tools";

// Nurse Pages
import NurseDashboard from "@/pages/nurse/dashboard";
import NurseGuidelines from "@/pages/nurse/guidelines";
import NurseGuidelineDetail from "@/pages/nurse/guidelines/detail";
import NurseConditions from "@/pages/nurse/conditions";
import NurseConditionDetail from "@/pages/nurse/conditions/detail";
import NursePharmacology from "@/pages/nurse/pharmacology";
import NursePharmacologyDetail from "@/pages/nurse/pharmacology/detail";
import NurseLearn from "@/pages/nurse/learn";
import NurseLearnModule from "@/pages/nurse/learn-module";
import NurseScenarios from "@/pages/nurse/scenarios";
import NurseScenarioDetail from "@/pages/nurse/scenarios/detail";
import NurseChat from "@/pages/nurse/chat";
import NurseAiGovernance from "@/pages/nurse/ai-governance";
import NurseSettings from "@/pages/nurse/settings";
import CompetencyOverview from "@/pages/nurse/competency/index";
import CompetencyDomain from "@/pages/nurse/competency/domain";
import CompetencyEntry from "@/pages/nurse/competency/entry";
import CompetencyPlan from "@/pages/nurse/competency/plan";
import CompetencyExport from "@/pages/nurse/competency/export";
import NurseEvents from "@/pages/nurse/events";
import NurseDirectory from "@/pages/nurse/directory";
import DoctorSmpc from "@/pages/doctor/smpc";
import PharmacistSmpc from "@/pages/pharmacist/smpc";
import NurseIncomingRequests from "@/pages/nurse/requests";
import NurseLogCall from "@/pages/nurse/log-call";
import NurseTeam from "@/pages/nurse/team";
import NurseJoinTeam from "@/pages/nurse/join-team";
import HcpSafetyReport from "@/pages/hcp/safety-report";
import HcpSafetyReports from "@/pages/hcp/safety-reports";
import HcpPractices from "@/pages/hcp/practices";
import CoordinatorDashboard from "@/pages/coordinator/dashboard";
import CoordinatorPatients from "@/pages/coordinator/patients";
import CoordinatorAppointments from "@/pages/coordinator/appointments";
import CoordinatorQueries from "@/pages/coordinator/queries";
import CoordinatorTeam from "@/pages/coordinator/team";
import CoordinatorInvitations from "@/pages/coordinator/invitations";
import JoinPractice from "@/pages/hcp/join-practice";
import InvitationAccept from "@/pages/invitations/accept";
import MedTrackerPage from "@/pages/med-tracker/index";
import PatientRecord from "@/pages/clinic/patient-record";
import PatientPreConsultSummaryPage from "@/pages/med-tracker/summary";
import NurseCareerHub from "@/pages/nurse/career/index";
import NurseBandProgression from "@/pages/nurse/career/band-progression";
import NurseNmcStandards from "@/pages/nurse/career/nmc-standards";
import NurseAnpAcp from "@/pages/nurse/career/anp-acp";
import NurseRcnSpecialist from "@/pages/nurse/career/rcn-specialist";
import NurseResearch from "@/pages/nurse/career/research";
import NurseLeadership from "@/pages/nurse/career/leadership";

// Doctor Pages
import DoctorDashboard from "@/pages/doctor/dashboard";
import DoctorDiagnosticCriteria from "@/pages/doctor/diagnostic-criteria";
import DoctorDiseaseActivity from "@/pages/doctor/disease-activity";
import DoctorPrescribing from "@/pages/doctor/prescribing";
import DoctorGpReferral from "@/pages/doctor/gp-referral";
import DoctorChat from "@/pages/doctor/chat";
import DoctorDictation from "@/pages/doctor/dictation";
import NurseDictation from "@/pages/nurse/dictation";
import DoctorSettings from "@/pages/doctor/settings";
import DoctorCpdLog from "@/pages/doctor/cpd/index";
import DoctorRevalidation from "@/pages/doctor/cpd/revalidation";
import DoctorCurriculum from "@/pages/doctor/cpd/curriculum";
import DoctorCareerHub from "@/pages/doctor/career/index";
import DoctorSubspecialties from "@/pages/doctor/career/subspecialties";
import DoctorAcademic from "@/pages/doctor/career/academic";
import DoctorConsultant from "@/pages/doctor/career/consultant";
import DoctorFellowships from "@/pages/doctor/career/fellowships";
import DoctorInterviewPrep from "@/pages/doctor/career/interview-prep";
import DoctorLearn from "@/pages/doctor/learn";
import DoctorLearnModule from "@/pages/doctor/learn-module";
import DoctorEvents from "@/pages/doctor/events";

// Pharmacist Pages
import PharmacistDashboard from "@/pages/pharmacist/dashboard";
import PharmacistMonitoring from "@/pages/pharmacist/monitoring";
import PharmacistDoseOptimisation from "@/pages/pharmacist/dose-optimisation";
import PharmacistBiosimilars from "@/pages/pharmacist/biosimilars";
import PharmacistInteractions from "@/pages/pharmacist/interactions";
import PharmacistCounselling from "@/pages/pharmacist/counselling";
import PharmacistNMP from "@/pages/pharmacist/nmp";
import PharmacistChat from "@/pages/pharmacist/chat";
import PharmacistSettings from "@/pages/pharmacist/settings";
import PharmacistCpdLog from "@/pages/pharmacist/cpd/index";
import PharmacistRevalidation from "@/pages/pharmacist/cpd/revalidation";
import PharmacistLearn from "@/pages/pharmacist/learn";
import PharmacistLearnModule from "@/pages/pharmacist/learn-module";
import PharmacistEvents from "@/pages/pharmacist/events";
import RpsFacultyPortfolio from "@/pages/pharmacist/cpd/rps-faculty";
import PrescribingCompetency from "@/pages/pharmacist/cpd/prescribing-competency";
import CppeLearning from "@/pages/pharmacist/cpd/cppe";
import PharmacistCareerHub from "@/pages/pharmacist/career/index";
import NhsBanding from "@/pages/pharmacist/career/nhs-banding";
import RpsCredentialing from "@/pages/pharmacist/career/rps-credentialing";
import FoundationToSpecialist from "@/pages/pharmacist/career/foundation-to-specialist";
import AcademicPharmacy from "@/pages/pharmacist/career/academic";
import ConsultantPharmacist from "@/pages/pharmacist/career/consultant";
import PharmacyLeadership from "@/pages/pharmacist/career/leadership";
import RpsMembership from "@/pages/pharmacist/career/rps-membership";

// Physio Pages
import PhysioDashboard from "@/pages/physio/dashboard";
import PhysioExerciseProtocols from "@/pages/physio/exercise-protocols";
import PhysioPatientLeaflets from "@/pages/physio/patient-leaflets";
import PhysioOutcomeMeasures from "@/pages/physio/outcome-measures";
import PhysioHydrotherapy from "@/pages/physio/hydrotherapy";
import PhysioManualTherapy from "@/pages/physio/manual-therapy";
import PhysioConditionProtocols from "@/pages/physio/condition-protocols";
import PhysioGuidelines from "@/pages/physio/guidelines";
import PhysioChat from "@/pages/physio/chat";
import PhysioSettings from "@/pages/physio/settings";
import PhysioCpdLog from "@/pages/physio/cpd/index";
import PhysioHcpcRevalidation from "@/pages/physio/cpd/revalidation";
import PhysioLearn from "@/pages/physio/learn";
import PhysioLearnModule from "@/pages/physio/learn-module";
import PhysioEvents from "@/pages/physio/events";
import CspFrameworkTracker from "@/pages/physio/cpd/csp-framework";
import AppPortfolio from "@/pages/physio/cpd/app-portfolio";
import AcprSpecialistSection from "@/pages/physio/cpd/acpr";
import IcspLearningLog from "@/pages/physio/cpd/icsp";
import ReflectionTool from "@/pages/physio/cpd/reflection";
import HcpcPortfolioTracker from "@/pages/physio/cpd/portfolio";
import PhysioCareerHub from "@/pages/physio/career/index";
import BandProgression from "@/pages/physio/career/band-progression";
import EspRoles from "@/pages/physio/career/esp";
import AppConsultantPathway from "@/pages/physio/career/app";
import AcademicPhysiotherapy from "@/pages/physio/career/academic";
import AcprSpecialistCareer from "@/pages/physio/career/acpr-specialist";
import PhysioLeadership from "@/pages/physio/career/leadership";

// OT Pages
import OTDashboard from "@/pages/ot/dashboard";
import OTHandAssessment from "@/pages/ot/hand-assessment";
import OTJointProtection from "@/pages/ot/joint-protection";
import OTFatigueManagement from "@/pages/ot/fatigue-management";
import OTADL from "@/pages/ot/adl";
import OTAssistiveTechnology from "@/pages/ot/assistive-technology";
import OTOutcomeMeasures from "@/pages/ot/outcome-measures";
import OTGuidelines from "@/pages/ot/guidelines";
import OTChat from "@/pages/ot/chat";
import OTSettings from "@/pages/ot/settings";
import OtCpdLog from "@/pages/ot/cpd/index";
import OtHcpcRevalidation from "@/pages/ot/cpd/revalidation";
import OTLearn from "@/pages/ot/learn";
import OTLearnModule from "@/pages/ot/learn-module";
import OTEvents from "@/pages/ot/events";
import RcotFrameworkTracker from "@/pages/ot/cpd/rcot-framework";
import OtAdvancedPractice from "@/pages/ot/cpd/advanced-practice";
import OtSpecialistSection from "@/pages/ot/cpd/specialist-section";
import RcotCollegeCpd from "@/pages/ot/cpd/rcot-learning";
import OtReflectionTool from "@/pages/ot/cpd/reflection";
import OtCareerHub from "@/pages/ot/career/index";
import OtBandProgression from "@/pages/ot/career/band-progression";
import OtConsultant from "@/pages/ot/career/consultant";
import OtFrcot from "@/pages/ot/career/frcot";
import OtAcademic from "@/pages/ot/career/academic";
import OtSsRheumatologyCareer from "@/pages/ot/career/ss-rheumatology";

// Admin Pages
import AdminDashboard from "@/pages/admin/dashboard";
import AdminRetentionSettings from "@/pages/admin/settings-retention";
import AdminGate from "@/pages/admin/AdminGate";
import AdminConditions from "@/pages/admin/conditions";
import AdminMedications from "@/pages/admin/medications";
import AdminGuidelines from "@/pages/admin/guidelines";
import AdminScenarios from "@/pages/admin/scenarios";
import AdminResources from "@/pages/admin/resources";
import AdminHospitals from "@/pages/admin/hospitals";
import AdminInboxes from "@/pages/admin/inboxes";
import AdminTeams from "@/pages/admin/teams";
import AdvicelineAdmin from "@/pages/admin/adviceline-dashboard";
import AdminSupplyCompanies from "@/pages/admin/supply-companies";
import AdminModules from "@/pages/admin/modules";
import AdminPromotions from "@/pages/admin/promotions";
import AdminHcpVerifications from "@/pages/admin/hcp-verifications";
import AdminNhsOrganisations from "@/pages/admin/nhs-organisations";
import AdminEvents from "@/pages/admin/events";
import AdminSafetyReports from "@/pages/admin/safety-reports";
import AdminUsers from "@/pages/admin/users";

// Approval gate
import PendingApproval from "@/pages/pending-approval";

// Patient Pages
import PatientDashboard from "@/pages/patient/dashboard";
import PatientConditions from "@/pages/patient/conditions";
import PatientConditionDetail from "@/pages/patient/conditions/detail";
import PatientMedicationDetail from "@/pages/patient/medications/detail";
import PatientKnowYourCondition from "@/pages/patient/know-your-condition";
import PatientKnowYourConditionDetail from "@/pages/patient/know-your-condition/detail";
import PatientKnowYourMedication from "@/pages/patient/know-your-medication";
import PatientKnowYourMedicationDetail from "@/pages/patient/know-your-medication/detail";
import PatientResources from "@/pages/patient/resources";
import PatientExercises from "@/pages/patient/exercises";
import PatientChat from "@/pages/patient/chat";
import PatientTriage from "@/pages/patient/triage";
import PatientSettings from "@/pages/patient/settings";
import PatientRequests from "@/pages/patient/requests";
import AdviceFaq from "@/pages/patient/advice-faq";
import PatientSafeguarding from "@/pages/patient/safeguarding";
import ClinicalSafetyPage from "@/pages/clinical-safety";
import AccessibilityStatement from "@/pages/accessibility";
import SignOffConfirm from "@/pages/sign-off/confirm";
import SearchPage from "@/pages/search";

const queryClient = new QueryClient();

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const clerkProxyUrl = (import.meta as any).env?.VITE_CLERK_PROXY_URL || "";
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

// Domain detection: patient features are only available on advicelinehub.co.uk
const isAdvicelineHub = typeof window !== "undefined" && window.location.hostname.includes("advicelinehub");

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath) ? path.slice(basePath.length) || "/" : path;
}

if (!clerkPubKey) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY in .env file");
}

const clerkAppearance = {
  theme: shadcn,
  cssLayerName: "clerk",
  options: {
    logoPlacement: "inside" as const,
    logoLinkUrl: basePath || "/",
    logoImageUrl: `${window.location.origin}${basePath}/logo.png`,
  },
  variables: {
    colorPrimary: "hsl(180 73% 30%)",
    colorForeground: "hsl(220 50% 10%)",
    colorMutedForeground: "hsl(215 16% 47%)",
    colorDanger: "hsl(0 84% 60%)",
    colorBackground: "hsl(0 0% 100%)",
    colorInput: "hsl(214 32% 91%)",
    colorInputForeground: "hsl(220 50% 10%)",
    colorNeutral: "hsl(214 32% 91%)",
    fontFamily: "'Inter', sans-serif",
    borderRadius: "0.5rem",
  },
  elements: {
    rootBox: "w-full flex justify-center",
    cardBox: "bg-white rounded-2xl w-[440px] max-w-full overflow-hidden shadow-lg",
    card: "!shadow-none !border-0 !bg-transparent !rounded-none",
    footer: "!shadow-none !border-0 !bg-transparent !rounded-none",
    headerTitle: "text-2xl font-bold text-slate-900",
    headerSubtitle: "text-slate-500",
    socialButtonsBlockButtonText: "text-slate-700 font-medium",
    formFieldLabel: "text-slate-700 font-medium",
    footerActionLink: "text-teal-700 hover:text-teal-800 font-medium",
    footerActionText: "text-slate-500",
    dividerText: "text-slate-400",
    identityPreviewEditButton: "text-teal-700 hover:text-teal-800",
    formFieldSuccessText: "text-teal-600",
    alertText: "text-red-600",
    logoBox: "flex justify-center mb-4",
    logoImage: "h-12 w-auto",
    socialButtonsBlockButton: "border border-slate-200 hover:bg-slate-50",
    formButtonPrimary: "bg-teal-700 hover:bg-teal-800 text-white font-medium",
    formFieldInput: "bg-slate-50 border border-slate-200 text-slate-900 focus:ring-2 focus:ring-teal-500",
    footerAction: "mt-4",
    dividerLine: "bg-slate-200",
    alert: "bg-red-50 border border-red-200",
    otpCodeFieldInput: "bg-slate-50 border border-slate-200 text-slate-900 focus:ring-2 focus:ring-teal-500",
    formFieldRow: "mb-4",
    main: "p-6",
  },
};

function SignInPage() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <SignIn routing="path" path={`${basePath}/sign-in`} signUpUrl={`${basePath}/sign-up`} />
        <p className="text-center text-xs text-slate-400 mt-4">
          By signing in you agree to our{" "}
          <a href={`${basePath}/terms`} className="underline hover:text-slate-600">Terms of Use</a>
          {" "}and{" "}
          <a href={`${basePath}/privacy`} className="underline hover:text-slate-600">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}

function SignUpPage() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <SignUp routing="path" path={`${basePath}/sign-up`} signInUrl={`${basePath}/sign-in`} />
        <p className="text-center text-xs text-slate-400 mt-4">
          By creating an account you agree to our{" "}
          <a href={`${basePath}/terms`} className="underline hover:text-slate-600">Terms of Use</a>
          {" "}and{" "}
          <a href={`${basePath}/privacy`} className="underline hover:text-slate-600">Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}

function ClerkAuthTokenBridge() {
  const { getToken } = useAuth();
  // Synchronous assignment during render — runs before any useEffect fires,
  // so the first React Query fetch already has access to the token getter.
  _clerkTokenRef.getToken = getToken;
  return null;
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const qc = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (prevUserIdRef.current !== undefined && prevUserIdRef.current !== userId) {
        qc.clear();
        // Shared-device safety: wipe per-user localStorage state whenever the
        // Clerk user changes (sign-out, or signing in as a different user on
        // the same browser). Mirrors the in-memory React Query cache clear.
        clearRheumHubLocalStorage();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, qc]);

  return null;
}

function HomeRedirect() {
  const { isSignedIn, isLoaded } = useAuth();
  const { data: profileCheck, isLoading: approvalLoading, isError: approvalError } = useApprovalStatus(isSignedIn === true);

  if (!isLoaded) return null;

  // Guest mode — unauthenticated but user chose to browse without signing in
  if (!isSignedIn) {
    const guestRole = localStorage.getItem("rheumhub_role");
    if (guestRole === "guest") {
      if (isAdvicelineHub) return <Redirect to="/patient" />;
      return <Redirect to="/clinical-tools" />;
    }
  }

  if (isSignedIn) {
    if (approvalLoading) return null;

    // Fail-closed: if we can't determine approval status, block at pending screen
    if (approvalError) return <Redirect to="/pending-approval" />;

    // Prefer the server-side role from user_profiles.role when present —
    // this means a signed-in user with a chosen role lands directly on
    // their portal even on a fresh device / cleared storage, instead of
    // being asked to pick again. Falls back to localStorage (for accounts
    // whose profile hasn't reloaded yet) and finally to /choose-role for
    // brand-new users with no profile.
    //
    // Admin accounts follow the same rule: if they have a server-side
    // role, honour it; otherwise let them pick freely. (Admins can still
    // switch via /choose-role at any time.)
    const KNOWN_ROUTES: Record<string, string> = {
      nurse: "/nurse",
      ...(isAdvicelineHub ? { patient: "/patient" } : {}),
      doctor: "/doctor",
      pharmacist: "/pharmacist",
      physio: "/physio",
      ot: "/ot",
      coordinator: "/coordinator",
    };

    const serverRole = profileCheck?.role ?? null;
    const localRole = localStorage.getItem("rheumhub_role");

    // Sync localStorage to the server-of-truth role when they differ.
    // Skip when localRole === "guest" (an explicit guest browse choice
    // that should not be overwritten by a stale profile fetch) and skip
    // during admin role-switching (admin chose a different role locally).
    if (serverRole && !profileCheck?.isAdmin && localRole !== "guest" && localRole !== serverRole) {
      localStorage.setItem("rheumhub_role", serverRole);
    }

    // Admins always go through /choose-role on home so they can pick which
    // portal to inspect. Only honour an explicit local choice they've
    // already made this session — never auto-route based on their own
    // user_profiles.role (admins typically have a placeholder role like
    // "nurse" in the DB that isn't meant to drive navigation).
    // Exception: on advicelinehub, there's only one portal — skip role picker.
    if (profileCheck?.isAdmin) {
      if (isAdvicelineHub) {
        // On Advice Line Hub, admins are nurses first — go to requests
        return <Redirect to="/nurse" />;
      }
      if (localRole && KNOWN_ROUTES[localRole]) return <Redirect to={KNOWN_ROUTES[localRole]} />;
      return <Redirect to="/choose-role" />;
    }

    if (profileCheck?.approvalStatus === "rejected") return <Redirect to="/pending-approval?rejected=1" />;
    if (profileCheck?.approvalStatus === "pending") return <Redirect to="/pending-approval" />;

    // No profile yet (brand-new user) — send to role selection
    if (profileCheck?.hasProfile === false || profileCheck?.onboardingComplete === false) return <Redirect to="/get-started" />;

    const effectiveRole = serverRole ?? localRole;
    if (effectiveRole && KNOWN_ROUTES[effectiveRole]) return <Redirect to={KNOWN_ROUTES[effectiveRole]} />;
    return <Redirect to="/choose-role" />;
  }

  return <Landing />;
}

const HCP_ROLES = ["nurse", "doctor", "pharmacist", "physio", "ot"];

function RoleGate({ allowedRole, children }: { allowedRole: string; children: React.ReactNode }) {
  const { isSignedIn, isLoaded } = useAuth();

  // Dev-only E2E bypass: allows Playwright tests to render protected pages
  // without a real Clerk session. Never active in production builds.
  const e2eBypass =
    import.meta.env.DEV &&
    localStorage.getItem("__e2e_bypass_auth__") === "true";

  const { data: profileCheck, isLoading: approvalLoading, isError: approvalError } = useApprovalStatus(
    !e2eBypass && isSignedIn === true,
  );

  if (!e2eBypass) {
    if (!isLoaded) return null;
    if (!isSignedIn) return <Redirect to="/" />;
    if (approvalLoading) return null;
    // Fail-closed: on any fetch error, block access
    if (approvalError) return <Redirect to="/pending-approval" />;
    // Admins bypass all role and approval restrictions
    if (profileCheck?.isAdmin) {
      return (
        <>
          {HCP_ROLES.includes(allowedRole) && <ClinicalSafetyModal />}
          {children}
        </>
      );
    }
    if (profileCheck?.approvalStatus === "rejected") return <Redirect to="/pending-approval?rejected=1" />;
    if (profileCheck?.approvalStatus === "pending") return <Redirect to="/pending-approval" />;
  }

  // Fall back to the API role when localStorage is empty — prevents the
  // infinite ChooseRoleGuard ↔ RoleGate redirect loop (React error #185)
  // that occurs when a user completes onboarding, gets redirected to their
  // role portal, but localStorage was never written.
  const storedRole = localStorage.getItem("rheumhub_role");
  const role = storedRole || profileCheck?.role || null;
  if (storedRole && storedRole !== profileCheck?.role && profileCheck?.role && HCP_ROLES.includes(profileCheck.role)) {
    // Sync stale localStorage from the server-of-truth role
    localStorage.setItem("rheumhub_role", profileCheck.role);
  }
  if (role === allowedRole) {
    // Persist the API role so future RoleGate checks don't redirect
    if (!storedRole && profileCheck?.role) {
      localStorage.setItem("rheumhub_role", profileCheck.role);
    }
    return (
      <>
        {HCP_ROLES.includes(allowedRole) && <ClinicalSafetyModal />}
        {children}
      </>
    );
  }
  if (role) return <Redirect to={`/${role}`} />;
  return <Redirect to="/choose-role" />;
}

function HcpGate({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded } = useAuth();
  const { data: profileCheck, isLoading: approvalLoading, isError: approvalError } = useApprovalStatus(isSignedIn === true);

  if (!isLoaded) return null;
  if (!isSignedIn) return <Redirect to="/" />;
  if (approvalLoading) return null;
  // Fail-closed: on any fetch error, block access
  if (approvalError) return <Redirect to="/pending-approval" />;
  // Admins bypass all restrictions
  if (profileCheck?.isAdmin) return <>{children}</>;
  if (profileCheck?.approvalStatus === "rejected") return <Redirect to="/pending-approval?rejected=1" />;
  if (profileCheck?.approvalStatus === "pending") return <Redirect to="/pending-approval" />;

  const role = localStorage.getItem("rheumhub_role");
  if (role && HCP_ROLES.includes(role)) return <>{children}</>;
  if (role) return <Redirect to={`/${role}`} />;
  return <Redirect to="/choose-role" />;
}

// Allows access to read-only patient content for both authenticated patients
// and unauthenticated guests (rheumhub_role === "guest" in localStorage).
function PatientOrGuestGate({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded } = useAuth();
  const role = localStorage.getItem("rheumhub_role");
  const isGuest = role === "guest";

  const e2eBypass =
    import.meta.env.DEV &&
    localStorage.getItem("__e2e_bypass_auth__") === "true";

  // Hooks must be called unconditionally — disable the query for guests
  const { data: profileCheck, isLoading: approvalLoading, isError: approvalError } = useApprovalStatus(
    !isGuest && !e2eBypass && isLoaded && isSignedIn === true,
  );

  // Guest path — no auth needed; render immediately
  if (isGuest) return <>{children}</>;

  // Auth path — same checks as RoleGate("patient")
  if (!e2eBypass) {
    if (!isLoaded) return null;
    if (!isSignedIn) return <Redirect to="/" />;
    if (approvalLoading) return null;
    if (approvalError) return <Redirect to="/pending-approval" />;
    // Admins bypass all restrictions
    if (profileCheck?.isAdmin) return <>{children}</>;
    if (profileCheck?.approvalStatus === "rejected") return <Redirect to="/pending-approval?rejected=1" />;
    if (profileCheck?.approvalStatus === "pending") return <Redirect to="/pending-approval" />;
  }

  if (role === "patient") return <>{children}</>;
  if (role) return <Redirect to={`/${role}`} />;
  return <Redirect to="/choose-role" />;
}

function ChooseRoleGuard() {
  const { isSignedIn, isLoaded } = useAuth();
  const { data: profileCheck, isLoading: approvalLoading } = useApprovalStatus(isSignedIn === true);

  if (!isLoaded || approvalLoading) return null;

  // Admins can always access choose-role to freely switch between portals
  if (isSignedIn && profileCheck?.isAdmin) return <ChooseRole />;

  // Non-admin users who already have a profile — route them appropriately
  // instead of letting them re-select a role and accidentally reset their
  // approval status.
  if (isSignedIn && profileCheck?.hasProfile) {
    if (profileCheck.approvalStatus === "rejected") return <Redirect to="/pending-approval?rejected=1" />;
    if (profileCheck.approvalStatus === "pending") return <Redirect to="/pending-approval" />;

    if (profileCheck.approvalStatus === "approved") {
      // Only redirect to a portal when localStorage tells us EXACTLY which
      // one the user belongs in. Any other value (null, "guest", a stale
      // role that RoleGate would reject, etc.) must fall through to the
      // role-picker UI. Previously this hardcoded a fallback to "/nurse",
      // which created an infinite ping-pong with RoleGate("nurse") for
      // approved non-nurse HCPs and for any approved user whose
      // rheumhub_role was missing — neither guard wrote localStorage, so
      // the cycle never broke. (See react.dev/errors/185 crashes.)
      const storedRole = localStorage.getItem("rheumhub_role");
      const apiRole = profileCheck.role;
      const role = storedRole || apiRole;
      const KNOWN_ROLES = ["nurse", ...(isAdvicelineHub ? ["patient"] : []), "doctor", "pharmacist", "physio", "ot", "coordinator"];
      if (role && KNOWN_ROLES.includes(role)) return <Redirect to={`/${role}`} />;
      return <ChooseRole />;
    }

    // Unknown approvalStatus on an existing profile — fail closed.
    return <Redirect to="/pending-approval" />;
  }

  return <ChooseRole />;
}

function OnboardingGuard() {
  const { isSignedIn, isLoaded } = useAuth();
  const { data: profileCheck, isLoading: approvalLoading, isError: approvalError } = useApprovalStatus(isSignedIn === true);

  if (!isLoaded) return null;
  if (!isSignedIn) return <Redirect to="/" />;
  if (approvalLoading) return null;
  // Fail-closed: on fetch error, block access
  if (approvalError) return <Redirect to="/pending-approval" />;
  if (profileCheck?.approvalStatus === "rejected") return <Redirect to="/pending-approval?rejected=1" />;
  if (profileCheck?.approvalStatus === "pending") return <Redirect to="/pending-approval" />;

  const role = localStorage.getItem("rheumhub_role");
  if (!role || role === "patient") return <Redirect to="/" />;
  return <Onboarding />;
}

function Router() {
  return (
    <>
      <PilotWelcomeBanner />
      <Switch>
      <Route path="/" component={HomeRedirect} />
      <Route path="/sign-in/*?" component={SignInPage} />
      <Route path="/sign-up/*?" component={SignUpPage} />
      <Route path="/get-started" component={OnboardingFlow} />
      <Route path="/choose-role" component={ChooseRoleGuard} />
      <Route path="/onboarding" component={OnboardingGuard} />

      {/* Search — accessible to any signed-in user or guest */}
      <Route path="/search" component={SearchPage} />

      {/* Public pages — no auth required */}
      <Route path="/privacy" component={PrivacyPolicy} />
      <Route path="/terms" component={TermsOfUse} />
      <Route path="/clinical-tools" component={ClinicalTools} />
      <Route path="/clinical-safety" component={ClinicalSafetyPage} />
      <Route path="/accessibility" component={AccessibilityStatement} />
      {isAdvicelineHub && <Route path="/patient/safeguarding" component={PatientSafeguarding} />}
      <Route path="/practices/join/:token" component={JoinPractice} />
      <Route path="/invite/accept" component={InvitationAccept} />
      <Route path="/nurse/practices"><RoleGate allowedRole="nurse"><HcpPractices /></RoleGate></Route>
      <Route path="/doctor/practices"><RoleGate allowedRole="doctor"><HcpPractices /></RoleGate></Route>
      <Route path="/pharmacist/practices"><RoleGate allowedRole="pharmacist"><HcpPractices /></RoleGate></Route>
      <Route path="/physio/practices"><RoleGate allowedRole="physio"><HcpPractices /></RoleGate></Route>
      <Route path="/ot/practices"><RoleGate allowedRole="ot"><HcpPractices /></RoleGate></Route>
      <Route path="/sign-off/:token" component={SignOffConfirm} />

      {/* Nurse Routes */}
      <Route path="/nurse">
        <RoleGate allowedRole="nurse"><NurseDashboard /></RoleGate>
      </Route>
      <Route path="/nurse/guidelines">
        <RoleGate allowedRole="nurse"><NurseGuidelines /></RoleGate>
      </Route>
      <Route path="/nurse/guidelines/:id">
        <HcpGate><NurseGuidelineDetail /></HcpGate>
      </Route>
      <Route path="/nurse/conditions">
        <RoleGate allowedRole="nurse"><NurseConditions /></RoleGate>
      </Route>
      <Route path="/nurse/conditions/:id">
        <HcpGate><NurseConditionDetail /></HcpGate>
      </Route>
      <Route path="/nurse/pharmacology">
        <RoleGate allowedRole="nurse"><NursePharmacology /></RoleGate>
      </Route>
      <Route path="/nurse/pharmacology/:id">
        <HcpGate><NursePharmacologyDetail /></HcpGate>
      </Route>
      <Route path="/nurse/learn">
        <RoleGate allowedRole="nurse"><NurseLearn /></RoleGate>
      </Route>
      <Route path="/nurse/learn/modules/:id">
        <RoleGate allowedRole="nurse"><NurseLearnModule /></RoleGate>
      </Route>
      {/* Legacy redirects for old medications URLs */}
      <Route path="/nurse/medications">
        <Redirect to="/nurse/pharmacology" />
      </Route>
      <Route path="/nurse/medications/:id">
        {(params) => <Redirect to={`/nurse/pharmacology/${params.id}`} />}
      </Route>
      <Route path="/nurse/scenarios">
        <RoleGate allowedRole="nurse"><NurseScenarios /></RoleGate>
      </Route>
      <Route path="/nurse/scenarios/:id">
        <HcpGate><NurseScenarioDetail /></HcpGate>
      </Route>
      <Route path="/nurse/chat">
        <RoleGate allowedRole="nurse"><NurseChat /></RoleGate>
      </Route>
      <Route path="/nurse/dictation">
        <RoleGate allowedRole="nurse"><NurseDictation /></RoleGate>
      </Route>
      <Route path="/doctor/dictation">
        <RoleGate allowedRole="doctor"><DoctorDictation /></RoleGate>
      </Route>
      <Route path="/nurse/ai-governance">
        <RoleGate allowedRole="nurse"><NurseAiGovernance /></RoleGate>
      </Route>
      <Route path="/hcp/safety-report">
        <HcpGate><HcpSafetyReport /></HcpGate>
      </Route>
      <Route path="/hcp/safety-reports">
        <HcpGate><HcpSafetyReports /></HcpGate>
      </Route>
      {isAdvicelineHub && (
        <>
          <Route path="/med-tracker">
            <HcpGate><MedTrackerPage /></HcpGate>
          </Route>
          <Route path="/med-tracker/patients/:id/summary">
            <HcpGate><PatientPreConsultSummaryPage /></HcpGate>
          </Route>
        </>
      )}
      <Route path="/clinic/patients/:id">
        <HcpGate><PatientRecord /></HcpGate>
      </Route>
      <Route path="/nurse/profile">
        <RoleGate allowedRole="nurse"><ProfileEditor /></RoleGate>
      </Route>
      <Route path="/nurse/settings">
        <RoleGate allowedRole="nurse"><NurseSettings /></RoleGate>
      </Route>
      <Route path="/nurse/competency">
        <RoleGate allowedRole="nurse"><CompetencyOverview /></RoleGate>
      </Route>
      <Route path="/nurse/competency/plan">
        <RoleGate allowedRole="nurse"><CompetencyPlan /></RoleGate>
      </Route>
      <Route path="/nurse/competency/export">
        <RoleGate allowedRole="nurse"><CompetencyExport /></RoleGate>
      </Route>
      <Route path="/nurse/portfolio/export">
        <RoleGate allowedRole="nurse"><CompetencyExport /></RoleGate>
      </Route>
      <Route path="/nurse/competency/:id">
        <RoleGate allowedRole="nurse"><CompetencyDomain /></RoleGate>
      </Route>
      <Route path="/nurse/competency/:id/:statementId">
        <RoleGate allowedRole="nurse"><CompetencyEntry /></RoleGate>
      </Route>
      <Route path="/nurse/events">
        <RoleGate allowedRole="nurse"><NurseEvents /></RoleGate>
      </Route>
      <Route path="/doctor/events">
        <RoleGate allowedRole="doctor"><DoctorEvents /></RoleGate>
      </Route>
      <Route path="/pharmacist/events">
        <RoleGate allowedRole="pharmacist"><PharmacistEvents /></RoleGate>
      </Route>
      <Route path="/physio/events">
        <RoleGate allowedRole="physio"><PhysioEvents /></RoleGate>
      </Route>
      <Route path="/ot/events">
        <RoleGate allowedRole="ot"><OTEvents /></RoleGate>
      </Route>
      <Route path="/nurse/directory">
        <RoleGate allowedRole="nurse"><NurseDirectory /></RoleGate>
      </Route>
      <Route path="/nurse/requests">
        <RoleGate allowedRole="nurse"><NurseIncomingRequests /></RoleGate>
      </Route>
      <Route path="/nurse/log-call">
        <RoleGate allowedRole="nurse"><NurseLogCall /></RoleGate>
      </Route>
      <Route path="/nurse/team">
        <RoleGate allowedRole="nurse"><NurseTeam /></RoleGate>
      </Route>
      <Route path="/nurse/join-team">
        <RoleGate allowedRole="nurse"><NurseJoinTeam /></RoleGate>
      </Route>
      <Route path="/nurse/career">
        <RoleGate allowedRole="nurse"><NurseCareerHub /></RoleGate>
      </Route>
      <Route path="/nurse/career/band-progression">
        <RoleGate allowedRole="nurse"><NurseBandProgression /></RoleGate>
      </Route>
      <Route path="/nurse/career/nmc-standards">
        <RoleGate allowedRole="nurse"><NurseNmcStandards /></RoleGate>
      </Route>
      <Route path="/nurse/career/anp-acp">
        <RoleGate allowedRole="nurse"><NurseAnpAcp /></RoleGate>
      </Route>
      <Route path="/nurse/career/rcn-specialist">
        <RoleGate allowedRole="nurse"><NurseRcnSpecialist /></RoleGate>
      </Route>
      <Route path="/nurse/career/research">
        <RoleGate allowedRole="nurse"><NurseResearch /></RoleGate>
      </Route>
      <Route path="/nurse/career/leadership">
        <RoleGate allowedRole="nurse"><NurseLeadership /></RoleGate>
      </Route>

      {/* Doctor Routes */}
      <Route path="/doctor">
        <RoleGate allowedRole="doctor"><DoctorDashboard /></RoleGate>
      </Route>
      <Route path="/doctor/diagnostic-criteria">
        <RoleGate allowedRole="doctor"><DoctorDiagnosticCriteria /></RoleGate>
      </Route>
      <Route path="/doctor/disease-activity">
        <RoleGate allowedRole="doctor"><DoctorDiseaseActivity /></RoleGate>
      </Route>
      <Route path="/doctor/prescribing">
        <RoleGate allowedRole="doctor"><DoctorPrescribing /></RoleGate>
      </Route>
      <Route path="/doctor/gp-referral">
        <RoleGate allowedRole="doctor"><DoctorGpReferral /></RoleGate>
      </Route>
      <Route path="/doctor/chat">
        <RoleGate allowedRole="doctor"><DoctorChat /></RoleGate>
      </Route>
      <Route path="/doctor/profile">
        <RoleGate allowedRole="doctor"><ProfileEditor /></RoleGate>
      </Route>
      <Route path="/doctor/settings">
        <RoleGate allowedRole="doctor"><DoctorSettings /></RoleGate>
      </Route>
      <Route path="/doctor/cpd">
        <RoleGate allowedRole="doctor"><DoctorCpdLog /></RoleGate>
      </Route>
      <Route path="/doctor/cpd/revalidation">
        <RoleGate allowedRole="doctor"><DoctorRevalidation /></RoleGate>
      </Route>
      <Route path="/doctor/cpd/curriculum">
        <RoleGate allowedRole="doctor"><DoctorCurriculum /></RoleGate>
      </Route>
      <Route path="/doctor/career">
        <RoleGate allowedRole="doctor"><DoctorCareerHub /></RoleGate>
      </Route>
      <Route path="/doctor/career/subspecialties">
        <RoleGate allowedRole="doctor"><DoctorSubspecialties /></RoleGate>
      </Route>
      <Route path="/doctor/career/academic">
        <RoleGate allowedRole="doctor"><DoctorAcademic /></RoleGate>
      </Route>
      <Route path="/doctor/career/consultant">
        <RoleGate allowedRole="doctor"><DoctorConsultant /></RoleGate>
      </Route>
      <Route path="/doctor/career/fellowships">
        <RoleGate allowedRole="doctor"><DoctorFellowships /></RoleGate>
      </Route>
      <Route path="/doctor/career/interview-prep">
        <RoleGate allowedRole="doctor"><DoctorInterviewPrep /></RoleGate>
      </Route>
      <Route path="/doctor/smpc">
        <RoleGate allowedRole="doctor"><DoctorSmpc /></RoleGate>
      </Route>
      <Route path="/doctor/learn">
        <RoleGate allowedRole="doctor"><DoctorLearn /></RoleGate>
      </Route>
      <Route path="/doctor/learn/modules/:id">
        <RoleGate allowedRole="doctor"><DoctorLearnModule /></RoleGate>
      </Route>

      {/* Pharmacist Routes */}
      <Route path="/pharmacist">
        <RoleGate allowedRole="pharmacist"><PharmacistDashboard /></RoleGate>
      </Route>
      <Route path="/pharmacist/monitoring">
        <RoleGate allowedRole="pharmacist"><PharmacistMonitoring /></RoleGate>
      </Route>
      <Route path="/pharmacist/dose-optimisation">
        <RoleGate allowedRole="pharmacist"><PharmacistDoseOptimisation /></RoleGate>
      </Route>
      <Route path="/pharmacist/biosimilars">
        <RoleGate allowedRole="pharmacist"><PharmacistBiosimilars /></RoleGate>
      </Route>
      <Route path="/pharmacist/interactions">
        <RoleGate allowedRole="pharmacist"><PharmacistInteractions /></RoleGate>
      </Route>
      <Route path="/pharmacist/counselling">
        <RoleGate allowedRole="pharmacist"><PharmacistCounselling /></RoleGate>
      </Route>
      <Route path="/pharmacist/nmp">
        <RoleGate allowedRole="pharmacist"><PharmacistNMP /></RoleGate>
      </Route>
      <Route path="/pharmacist/chat">
        <RoleGate allowedRole="pharmacist"><PharmacistChat /></RoleGate>
      </Route>
      <Route path="/pharmacist/profile">
        <RoleGate allowedRole="pharmacist"><ProfileEditor /></RoleGate>
      </Route>
      <Route path="/pharmacist/settings">
        <RoleGate allowedRole="pharmacist"><PharmacistSettings /></RoleGate>
      </Route>
      <Route path="/pharmacist/cpd">
        <RoleGate allowedRole="pharmacist"><PharmacistCpdLog /></RoleGate>
      </Route>
      <Route path="/pharmacist/cpd/revalidation">
        <RoleGate allowedRole="pharmacist"><PharmacistRevalidation /></RoleGate>
      </Route>
      <Route path="/pharmacist/cpd/rps-faculty">
        <RoleGate allowedRole="pharmacist"><RpsFacultyPortfolio /></RoleGate>
      </Route>
      <Route path="/pharmacist/cpd/prescribing-competency">
        <RoleGate allowedRole="pharmacist"><PrescribingCompetency /></RoleGate>
      </Route>
      <Route path="/pharmacist/cpd/cppe">
        <RoleGate allowedRole="pharmacist"><CppeLearning /></RoleGate>
      </Route>
      <Route path="/pharmacist/career">
        <RoleGate allowedRole="pharmacist"><PharmacistCareerHub /></RoleGate>
      </Route>
      <Route path="/pharmacist/career/nhs-banding">
        <RoleGate allowedRole="pharmacist"><NhsBanding /></RoleGate>
      </Route>
      <Route path="/pharmacist/career/rps-credentialing">
        <RoleGate allowedRole="pharmacist"><RpsCredentialing /></RoleGate>
      </Route>
      <Route path="/pharmacist/career/foundation-to-specialist">
        <RoleGate allowedRole="pharmacist"><FoundationToSpecialist /></RoleGate>
      </Route>
      <Route path="/pharmacist/career/academic">
        <RoleGate allowedRole="pharmacist"><AcademicPharmacy /></RoleGate>
      </Route>
      <Route path="/pharmacist/career/consultant">
        <RoleGate allowedRole="pharmacist"><ConsultantPharmacist /></RoleGate>
      </Route>
      <Route path="/pharmacist/career/leadership">
        <RoleGate allowedRole="pharmacist"><PharmacyLeadership /></RoleGate>
      </Route>
      <Route path="/pharmacist/career/rps-membership">
        <RoleGate allowedRole="pharmacist"><RpsMembership /></RoleGate>
      </Route>
      <Route path="/pharmacist/smpc">
        <RoleGate allowedRole="pharmacist"><PharmacistSmpc /></RoleGate>
      </Route>
      <Route path="/pharmacist/learn">
        <RoleGate allowedRole="pharmacist"><PharmacistLearn /></RoleGate>
      </Route>
      <Route path="/pharmacist/learn/modules/:id">
        <RoleGate allowedRole="pharmacist"><PharmacistLearnModule /></RoleGate>
      </Route>

      {/* Physio Routes */}
      <Route path="/physio">
        <RoleGate allowedRole="physio"><PhysioDashboard /></RoleGate>
      </Route>
      <Route path="/physio/exercise-protocols">
        <RoleGate allowedRole="physio"><PhysioExerciseProtocols /></RoleGate>
      </Route>
      <Route path="/physio/patient-leaflets">
        <RoleGate allowedRole="physio"><PhysioPatientLeaflets /></RoleGate>
      </Route>
      <Route path="/physio/outcome-measures">
        <RoleGate allowedRole="physio"><PhysioOutcomeMeasures /></RoleGate>
      </Route>
      <Route path="/physio/hydrotherapy">
        <RoleGate allowedRole="physio"><PhysioHydrotherapy /></RoleGate>
      </Route>
      <Route path="/physio/manual-therapy">
        <RoleGate allowedRole="physio"><PhysioManualTherapy /></RoleGate>
      </Route>
      <Route path="/physio/condition-protocols">
        <RoleGate allowedRole="physio"><PhysioConditionProtocols /></RoleGate>
      </Route>
      <Route path="/physio/guidelines">
        <RoleGate allowedRole="physio"><PhysioGuidelines /></RoleGate>
      </Route>
      <Route path="/physio/chat">
        <RoleGate allowedRole="physio"><PhysioChat /></RoleGate>
      </Route>
      <Route path="/physio/profile">
        <RoleGate allowedRole="physio"><ProfileEditor /></RoleGate>
      </Route>
      <Route path="/physio/settings">
        <RoleGate allowedRole="physio"><PhysioSettings /></RoleGate>
      </Route>
      <Route path="/physio/cpd">
        <RoleGate allowedRole="physio"><PhysioCpdLog /></RoleGate>
      </Route>
      <Route path="/physio/cpd/revalidation">
        <RoleGate allowedRole="physio"><PhysioHcpcRevalidation /></RoleGate>
      </Route>
      <Route path="/physio/cpd/csp-framework">
        <RoleGate allowedRole="physio"><CspFrameworkTracker /></RoleGate>
      </Route>
      <Route path="/physio/cpd/app-portfolio">
        <RoleGate allowedRole="physio"><AppPortfolio /></RoleGate>
      </Route>
      <Route path="/physio/cpd/acpr">
        <RoleGate allowedRole="physio"><AcprSpecialistSection /></RoleGate>
      </Route>
      <Route path="/physio/cpd/icsp">
        <RoleGate allowedRole="physio"><IcspLearningLog /></RoleGate>
      </Route>
      <Route path="/physio/cpd/reflection">
        <RoleGate allowedRole="physio"><ReflectionTool /></RoleGate>
      </Route>
      <Route path="/physio/cpd/portfolio">
        <RoleGate allowedRole="physio"><HcpcPortfolioTracker /></RoleGate>
      </Route>
      <Route path="/physio/career">
        <RoleGate allowedRole="physio"><PhysioCareerHub /></RoleGate>
      </Route>
      <Route path="/physio/career/band-progression">
        <RoleGate allowedRole="physio"><BandProgression /></RoleGate>
      </Route>
      <Route path="/physio/career/esp">
        <RoleGate allowedRole="physio"><EspRoles /></RoleGate>
      </Route>
      <Route path="/physio/career/app">
        <RoleGate allowedRole="physio"><AppConsultantPathway /></RoleGate>
      </Route>
      <Route path="/physio/career/academic">
        <RoleGate allowedRole="physio"><AcademicPhysiotherapy /></RoleGate>
      </Route>
      <Route path="/physio/career/acpr-specialist">
        <RoleGate allowedRole="physio"><AcprSpecialistCareer /></RoleGate>
      </Route>
      <Route path="/physio/career/leadership">
        <RoleGate allowedRole="physio"><PhysioLeadership /></RoleGate>
      </Route>
      <Route path="/physio/learn">
        <RoleGate allowedRole="physio"><PhysioLearn /></RoleGate>
      </Route>
      <Route path="/physio/learn/modules/:id">
        <RoleGate allowedRole="physio"><PhysioLearnModule /></RoleGate>
      </Route>

      {/* OT Routes */}
      <Route path="/ot">
        <RoleGate allowedRole="ot"><OTDashboard /></RoleGate>
      </Route>
      <Route path="/ot/hand-assessment">
        <RoleGate allowedRole="ot"><OTHandAssessment /></RoleGate>
      </Route>
      <Route path="/ot/joint-protection">
        <RoleGate allowedRole="ot"><OTJointProtection /></RoleGate>
      </Route>
      <Route path="/ot/fatigue-management">
        <RoleGate allowedRole="ot"><OTFatigueManagement /></RoleGate>
      </Route>
      <Route path="/ot/adl">
        <RoleGate allowedRole="ot"><OTADL /></RoleGate>
      </Route>
      <Route path="/ot/assistive-technology">
        <RoleGate allowedRole="ot"><OTAssistiveTechnology /></RoleGate>
      </Route>
      <Route path="/ot/outcome-measures">
        <RoleGate allowedRole="ot"><OTOutcomeMeasures /></RoleGate>
      </Route>
      <Route path="/ot/guidelines">
        <RoleGate allowedRole="ot"><OTGuidelines /></RoleGate>
      </Route>
      <Route path="/ot/chat">
        <RoleGate allowedRole="ot"><OTChat /></RoleGate>
      </Route>
      <Route path="/ot/profile">
        <RoleGate allowedRole="ot"><ProfileEditor /></RoleGate>
      </Route>
      <Route path="/ot/settings">
        <RoleGate allowedRole="ot"><OTSettings /></RoleGate>
      </Route>
      <Route path="/ot/cpd">
        <RoleGate allowedRole="ot"><OtCpdLog /></RoleGate>
      </Route>
      <Route path="/ot/cpd/revalidation">
        <RoleGate allowedRole="ot"><OtHcpcRevalidation /></RoleGate>
      </Route>
      <Route path="/ot/cpd/rcot-framework">
        <RoleGate allowedRole="ot"><RcotFrameworkTracker /></RoleGate>
      </Route>
      <Route path="/ot/cpd/advanced-practice">
        <RoleGate allowedRole="ot"><OtAdvancedPractice /></RoleGate>
      </Route>
      <Route path="/ot/cpd/specialist-section">
        <RoleGate allowedRole="ot"><OtSpecialistSection /></RoleGate>
      </Route>
      <Route path="/ot/cpd/rcot-learning">
        <RoleGate allowedRole="ot"><RcotCollegeCpd /></RoleGate>
      </Route>
      <Route path="/ot/cpd/reflection">
        <RoleGate allowedRole="ot"><OtReflectionTool /></RoleGate>
      </Route>
      <Route path="/ot/career">
        <RoleGate allowedRole="ot"><OtCareerHub /></RoleGate>
      </Route>
      <Route path="/ot/career/band-progression">
        <RoleGate allowedRole="ot"><OtBandProgression /></RoleGate>
      </Route>
      <Route path="/ot/career/consultant">
        <RoleGate allowedRole="ot"><OtConsultant /></RoleGate>
      </Route>
      <Route path="/ot/career/frcot">
        <RoleGate allowedRole="ot"><OtFrcot /></RoleGate>
      </Route>
      <Route path="/ot/career/academic">
        <RoleGate allowedRole="ot"><OtAcademic /></RoleGate>
      </Route>
      <Route path="/ot/career/ss-rheumatology">
        <RoleGate allowedRole="ot"><OtSsRheumatologyCareer /></RoleGate>
      </Route>
      <Route path="/ot/learn">
        <RoleGate allowedRole="ot"><OTLearn /></RoleGate>
      </Route>
      <Route path="/ot/learn/modules/:id">
        <RoleGate allowedRole="ot"><OTLearnModule /></RoleGate>
      </Route>

      {/* Coordinator Routes — non-clinical practice admin */}
      <Route path="/coordinator">
        <RoleGate allowedRole="coordinator"><CoordinatorDashboard /></RoleGate>
      </Route>
      <Route path="/coordinator/patients">
        <RoleGate allowedRole="coordinator"><CoordinatorPatients /></RoleGate>
      </Route>
      <Route path="/coordinator/appointments">
        <RoleGate allowedRole="coordinator"><CoordinatorAppointments /></RoleGate>
      </Route>
      <Route path="/coordinator/queries">
        <RoleGate allowedRole="coordinator"><CoordinatorQueries /></RoleGate>
      </Route>
      <Route path="/coordinator/team">
        <RoleGate allowedRole="coordinator"><CoordinatorTeam /></RoleGate>
      </Route>
      <Route path="/coordinator/invitations">
        <RoleGate allowedRole="coordinator"><CoordinatorInvitations /></RoleGate>
      </Route>
      <Route path="/coordinator/practices">
        <RoleGate allowedRole="coordinator"><HcpPractices /></RoleGate>
      </Route>

      {/* Patient Routes — only available on advicelinehub.co.uk */}
      {isAdvicelineHub && (
        <>
          <Route path="/patient">
            <PatientOrGuestGate><PatientDashboard /></PatientOrGuestGate>
          </Route>
          <Route path="/patient/conditions">
            <PatientOrGuestGate><PatientConditions /></PatientOrGuestGate>
          </Route>
          <Route path="/patient/conditions/:id">
            <PatientOrGuestGate><PatientConditionDetail /></PatientOrGuestGate>
          </Route>
          <Route path="/patient/medications/:id">
            <PatientOrGuestGate><PatientMedicationDetail /></PatientOrGuestGate>
          </Route>
          <Route path="/patient/know-your-condition">
            <PatientOrGuestGate><PatientKnowYourCondition /></PatientOrGuestGate>
          </Route>
          <Route path="/patient/know-your-condition/:id">
            <PatientOrGuestGate><PatientKnowYourConditionDetail /></PatientOrGuestGate>
          </Route>
          <Route path="/patient/know-your-medication">
            <PatientOrGuestGate><PatientKnowYourMedication /></PatientOrGuestGate>
          </Route>
          <Route path="/patient/know-your-medication/:id">
            <PatientOrGuestGate><PatientKnowYourMedicationDetail /></PatientOrGuestGate>
          </Route>
          <Route path="/patient/resources">
            <PatientOrGuestGate><PatientResources /></PatientOrGuestGate>
          </Route>
          <Route path="/patient/exercises">
            <PatientOrGuestGate><PatientExercises /></PatientOrGuestGate>
          </Route>
          {/* Auth-required patient routes — guests are redirected to sign-in */}
          <Route path="/patient/chat">
            <RoleGate allowedRole="patient"><PatientChat /></RoleGate>
          </Route>
          <Route path="/patient/triage">
            <RoleGate allowedRole="patient"><PatientTriage /></RoleGate>
          </Route>
          <Route path="/patient/settings">
            <RoleGate allowedRole="patient"><PatientSettings /></RoleGate>
          </Route>
          <Route path="/patient/requests">
            <RoleGate allowedRole="patient"><PatientRequests /></RoleGate>
          </Route>
          <Route path="/patient/advice-faq">
            <RoleGate allowedRole="patient"><AdviceFaq /></RoleGate>
          </Route>
        </>
      )}

      {/* Admin Routes */}
      <Route path="/admin">
        <AdminGate><AdminDashboard /></AdminGate>
      </Route>
      <Route path="/admin/conditions">
        <AdminGate><AdminConditions /></AdminGate>
      </Route>
      <Route path="/admin/medications">
        <AdminGate><AdminMedications /></AdminGate>
      </Route>
      <Route path="/admin/guidelines">
        <AdminGate><AdminGuidelines /></AdminGate>
      </Route>
      <Route path="/admin/scenarios">
        <AdminGate><AdminScenarios /></AdminGate>
      </Route>
      <Route path="/admin/resources">
        <AdminGate><AdminResources /></AdminGate>
      </Route>
      <Route path="/admin/hospitals">
        <AdminGate><AdminHospitals /></AdminGate>
      </Route>
      <Route path="/admin/inboxes">
        <AdminGate><AdminInboxes /></AdminGate>
      </Route>
      <Route path="/admin/adviceline">
        <AdminGate><AdvicelineAdmin /></AdminGate>
      </Route>
      <Route path="/admin/teams">
        <AdminGate><AdminTeams /></AdminGate>
      </Route>
      <Route path="/admin/supply-companies">
        <AdminGate><AdminSupplyCompanies /></AdminGate>
      </Route>
      <Route path="/admin/modules">
        <AdminGate><AdminModules /></AdminGate>
      </Route>
      <Route path="/admin/hcp-verifications">
        <AdminGate><AdminHcpVerifications /></AdminGate>
      </Route>
      <Route path="/admin/nhs-organisations">
        <AdminGate><AdminNhsOrganisations /></AdminGate>
      </Route>
      <Route path="/admin/promotions">
        <AdminGate><AdminPromotions /></AdminGate>
      </Route>
      <Route path="/admin/events">
        <AdminGate><AdminEvents /></AdminGate>
      </Route>
      <Route path="/admin/safety-reports">
        <AdminGate><AdminSafetyReports /></AdminGate>
      </Route>
      <Route path="/admin/users">
        <AdminGate><AdminUsers /></AdminGate>
      </Route>
      <Route path="/admin/settings/retention">
        <AdminGate><AdminRetentionSettings /></AdminGate>
      </Route>

      <Route path="/pending-approval">
        {() => {
          const rejected = new URLSearchParams(window.location.search).get("rejected") === "1";
          return <PendingApproval rejected={rejected} />;
        }}
      </Route>

      <Route component={NotFound} />
    </Switch>
    </>
  );
}

function ClerkProviderWithRoutes() {
  const [, setLocation] = useLocation();

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      localization={{
        signIn: {
          start: {
            title: "Welcome back",
            subtitle: `Sign in to ${import.meta.env.VITE_APP_NAME || "RheumHub"}`,
          },
        },
        signUp: {
          start: {
            title: `Join ${import.meta.env.VITE_APP_NAME || "RheumHub"}`,
            subtitle: "Get started today",
          },
        },
      }}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <QueryClientProvider client={queryClient}>
        <ClerkAuthTokenBridge />
        <ClerkQueryClientCacheInvalidator />
        <TooltipProvider>
          <Router />
          <Toaster />
          <LocalStorageNotice />
        </TooltipProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <WouterRouter base={basePath}>
        <ClerkProviderWithRoutes />
      </WouterRouter>
    </ErrorBoundary>
  );
}

export default App;
